import { c as _c } from "react/compiler-runtime";
import { ReactNode } from "react";
import { FaFacebookF, FaInstagram, FaYoutube, FaWhatsapp, FaGooglePlay, FaApple } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { cn } from "@/lib/utils";

// Arattai icon component using actual logo image
const ArattaiIcon = t0 => {
  const $ = _c(4);
  const {
    size: t1,
    src
  } = t0;
  const size = t1 === undefined ? 16 : t1;
  const t2 = src || "https://media.dinamani.com/dinamani/2026-02-25/gjt6ao2j/Arattailogo.png";
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      objectFit: "contain"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== size || $[2] !== t2) {
    t4 = <img src={t2} alt="Arattai" width={size} height={size} loading="lazy" decoding="async" className="rounded-sm" style={t3} />;
    $[1] = size;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
};
export interface SocialLinkConfig {
  id: string;
  platform: string;
  url: string;
  iconUrl?: string;
}
type SocialIconProps = {
  className?: string;
  useGrid?: boolean;
  showAppIcons?: boolean;
  links?: SocialLinkConfig[];
};
interface IconRegistryEntry {
  icon: (color: string) => ReactNode;
  bgColor: string;
  isImage?: boolean;
}

// Known platforms get a brand icon + color; unrecognized platforms fall back
// to iconUrl (rendered as an <img>) or are skipped rather than rendering broken.
const ICON_REGISTRY: Record<string, IconRegistryEntry> = {
  facebook: {
    icon: color => <FaFacebookF size={16} color={color} />,
    bgColor: "#3B5998"
  },
  arattai: {
    icon: () => <ArattaiIcon size={24} />,
    bgColor: "#FFC107",
    isImage: true
  },
  instagram: {
    icon: color => <FaInstagram size={16} color={color} />,
    bgColor: "#D62976"
  },
  x: {
    icon: color => <FaXTwitter size={16} color={color} />,
    bgColor: "#000000"
  },
  twitter: {
    icon: color => <FaXTwitter size={16} color={color} />,
    bgColor: "#000000"
  },
  youtube: {
    icon: color => <FaYoutube size={16} color={color} />,
    bgColor: "#FF0200"
  },
  whatsapp: {
    icon: color => <FaWhatsapp size={16} color={color} />,
    bgColor: "#67C15E"
  },
  playstore: {
    icon: color => <FaGooglePlay size={16} color={color} />,
    bgColor: "#A4C639"
  },
  apple: {
    icon: color => <FaApple size={16} color={color} />,
    bgColor: "#000000"
  }
};
const normalizePlatform = (platform: string) => platform.toLowerCase().replace(/[^a-z]/g, "");

// Fallback set used when no `links` config is provided (e.g. Storybook usage
// or before the header-footer config has synced).
const DEFAULT_LINKS: SocialLinkConfig[] = [{
  id: "facebook",
  platform: "Facebook",
  url: "https://www.facebook.com/DinamaniDaily"
}, {
  id: "arattai",
  platform: "Arattai",
  url: "https://aratt.ai/@dinamani"
}, {
  id: "instagram",
  platform: "Instagram",
  url: "https://www.instagram.com/dinamanidaily/"
}, {
  id: "x",
  platform: "X",
  url: "https://x.com/DinamaniDaily"
}, {
  id: "youtube",
  platform: "YouTube",
  url: "https://www.youtube.com/channel/UC3jcdpf8dWtljex9PyhSM6w"
}, {
  id: "whatsapp",
  platform: "WhatsApp",
  url: "https://www.whatsapp.com/channel/0029Va60JxGFcowBIEtwvB0G"
}, {
  id: "playstore",
  platform: "Play Store",
  url: "https://play.google.com/store/apps/details?id=com.dinamani.android"
}, {
  id: "apple",
  platform: "Apple",
  url: "https://apps.apple.com/app/dinamani-tamil-news/id1244532821"
}];
const SocialIcons = t0 => {
  const $ = _c(16);
  const {
    className,
    useGrid: t1,
    showAppIcons: t2,
    links
  } = t0;
  const useGrid = t1 === undefined ? false : t1;
  const showAppIcons = t2 === undefined ? true : t2;
  let t3;
  let t4;
  if ($[0] !== className || $[1] !== links || $[2] !== showAppIcons || $[3] !== useGrid) {
    const source = links ?? (showAppIcons ? DEFAULT_LINKS : DEFAULT_LINKS.filter(_temp));
    let t5;
    if ($[6] !== useGrid) {
      t5 = link => {
        const registryEntry = ICON_REGISTRY[normalizePlatform(link.platform)];
        const color = useGrid ? registryEntry?.bgColor ?? "#1B1B1B" : "#FFFFFF";
        if (registryEntry) {
          return {
            key: link.id,
            href: link.url,
            label: link.platform,
            bgColor: registryEntry.bgColor,
            isImage: !!registryEntry.isImage,
            icon: registryEntry.isImage ? <ArattaiIcon size={24} src={link.iconUrl} /> : registryEntry.icon(color)
          };
        }
        if (link.iconUrl) {
          return {
            key: link.id,
            href: link.url,
            label: link.platform,
            bgColor: "#808080",
            isImage: true,
            icon: <img src={link.iconUrl} alt={link.platform} width={16} height={16} className="object-contain" />
          };
        }
        return null;
      };
      $[6] = useGrid;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    const resolved = source.map(t5).filter(_temp2);
    const t6 = useGrid ? "flex flex-wrap items-center gap-3" : "flex flex-wrap items-center gap-3";
    if ($[8] !== className || $[9] !== t6) {
      t3 = cn(t6, className);
      $[8] = className;
      $[9] = t6;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t7;
    if ($[11] !== useGrid) {
      t7 = item_0 => <li key={item_0.key}><a href={item_0.href} target="_blank" rel="noreferrer" aria-label={item_0.label} className={cn("inline-flex items-center justify-center shadow-sm hover:shadow transition-all", item_0.isImage ? "w-6 h-6 rounded-md overflow-hidden" : `w-6 h-6 rounded-full ${useGrid ? "bg-FFFFFF border border-DCDCDC" : ""}`)} style={!useGrid ? {
          backgroundColor: item_0.bgColor
        } : {}}>{item_0.icon}</a></li>;
      $[11] = useGrid;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    t4 = resolved.map(t7);
    $[0] = className;
    $[1] = links;
    $[2] = showAppIcons;
    $[3] = useGrid;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = <ul className={t3}>{t4}</ul>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
export default SocialIcons;
function _temp(l) {
  return l.platform !== "Play Store" && l.platform !== "Apple";
}
function _temp2(item) {
  return item !== null;
}